import { A } from "@solidjs/router";
import { BiSolidPlusCircle } from "solid-icons/bi";
import { IoChevronDownOutline, IoCloseOutline } from "solid-icons/io";
import { createSignal, For, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { useOptionalBillingContext } from "@/domains/billing/context/billing-provider";
import { useGetUserCompaniesV2 } from "@/domains/company/company/api";
import { useSelectWorkspace } from "@/domains/company/company/hooks/useSelectWorkspace";
import { useCompanyStore } from "@/domains/company/company/store/company-store";

interface CompanySelectorProps {
  class?: string;
}

const CompanyAvatar = (props: { name?: string; size?: "sm" | "md" }) => {
  const initials = () => props.name?.slice(0, 2)?.toUpperCase() ?? "";
  return (
    <div
      class="bg-primary rounded-md flex items-center justify-center shrink-0"
      classList={{
        "w-6 h-6": props.size === "sm" || !props.size,
        "w-7 h-7": props.size === "md",
      }}
    >
      <span class="text-primary-foreground font-semibold text-xs leading-none">
        {initials()}
      </span>
    </div>
  );
};

export const CompanySelector = (props: CompanySelectorProps) => {
  const [isOpen, setIsOpen] = createSignal(false);
  const companiesQuery = useGetUserCompaniesV2();
  const [auth] = useAuthV2();
  const [companyStore] = useCompanyStore();
  const { selectWorkspace } = useSelectWorkspace();
  const { t } = useI18n();
  const billingCtx = useOptionalBillingContext();
  const isReadOnly = () =>
    billingCtx?.billingStatusContract()?.billing_status === "unpaid";

  const selectedName = () => companyStore.selectedCompany?.name ?? "";
  const companies = () =>
    companiesQuery.data?.companies ?? auth.companies ?? [];
  const canCreateCompany = () =>
    auth.capabilities?.can_create_company_workspace !== false &&
    !auth.capabilities?.has_active_solo_freelancer;

  return (
    <Popover open={isOpen()} onOpenChange={setIsOpen}>
      <PopoverTrigger>
        <div
          class={`hidden md:flex items-center gap-2 h-9 px-3 rounded-lg border border-border bg-card cursor-pointer hover:bg-accent transition-colors duration-150 ${props.class ?? ""}`}
        >
          <CompanyAvatar name={selectedName()} />
          <span class="font-medium text-sm text-foreground max-w-40 truncate">
            {selectedName()}
          </span>
          <IoChevronDownOutline
            class="w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ml-0.5"
            classList={{ "rotate-180": isOpen() }}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent class="w-72 p-0 overflow-hidden" hideCloseButton>
        {/* Header */}
        <div class="flex items-center justify-between px-4 py-3">
          <p class="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
            {t("company.sidebar.companySelector.title")}
          </p>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            class="flex items-center justify-center w-6 h-6 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors duration-150"
          >
            <IoCloseOutline class="w-4 h-4" />
          </button>
        </div>

        <div class="border-t border-border" />

        {/* Companies section */}
        <div class="p-2">
          <p class="text-xs font-medium text-muted-foreground px-2 py-1.5">
            Workspace
          </p>
          <div class="flex flex-col gap-0.5">
            <For each={companies()}>
              {(company) => {
                const isActive = () =>
                  company.id === companyStore.selectedCompany?.id;
                return (
                  <button
                    type="button"
                    onClick={() => {
                      selectWorkspace(company);
                      setIsOpen(false);
                    }}
                    class="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left transition-colors duration-150"
                    classList={{
                      "bg-primary/5": isActive(),
                      "hover:bg-accent": !isActive(),
                    }}
                  >
                    <CompanyAvatar name={company.name} size="md" />
                    <div class="flex-1 min-w-0">
                      <div class="font-medium text-sm text-foreground truncate">
                        {company.name}
                      </div>
                      <Show when={company.description}>
                        <div class="text-xs text-muted-foreground truncate">
                          {company.description}
                        </div>
                      </Show>
                      <Show when={company.kind === "solo_freelancer"}>
                        <div class="text-xs text-muted-foreground truncate">
                          Freelance
                        </div>
                      </Show>
                    </div>
                    <Show when={isActive()}>
                      <div class="w-4 h-4 rounded-full bg-primary flex items-center justify-center shrink-0">
                        <svg
                          class="w-2.5 h-2.5 text-primary-foreground"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          aria-hidden="true"
                        >
                          <title>Selezionata</title>
                          <path
                            fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </div>
                    </Show>
                  </button>
                );
              }}
            </For>
          </div>
        </div>

        <div class="border-t border-border" />

        {/* Footer action */}
        <div class="p-2">
          <Tooltip openDelay={100} disabled={!isReadOnly()}>
            <TooltipTrigger as="div" class="w-full">
              {/* A link cannot be disabled: without the right to create one,
                  the entry is the disabled button and not a link to `#`. */}
              <Show
                when={!isReadOnly() && canCreateCompany()}
                fallback={
                  <Button
                    variant="ghost"
                    class="w-full justify-start text-sm text-muted-foreground h-9"
                    disabled
                    aria-disabled="true"
                  >
                    <BiSolidPlusCircle class="w-4 h-4 mr-2 text-primary" />
                    {t("company.sidebar.companySelector.create")}
                  </Button>
                }
              >
                <Button
                  as={A}
                  href="/company/create"
                  variant="ghost"
                  class="w-full justify-start text-sm text-muted-foreground h-9"
                >
                  <BiSolidPlusCircle class="w-4 h-4 mr-2 text-primary" />
                  {t("company.sidebar.companySelector.create")}
                </Button>
              </Show>
            </TooltipTrigger>
            <TooltipContent>{t("billing.read_only_tooltip")}</TooltipContent>
          </Tooltip>
        </div>
      </PopoverContent>
    </Popover>
  );
};
